import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowRight, FiUsers, FiClock, FiShield, FiStar } from 'react-icons/fi';
import { formsAPI } from '../services/api';
import { toast } from 'react-toastify';

const LandingContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const LandingCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 60px 40px;
  max-width: 800px;
  width: 100%;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #0ea5e9, #0284c7);
  }
`;

const LogoContainer = styled.div`
  margin-bottom: 40px;
  
  img {
    max-width: 200px;
    max-height: 100px;
    object-fit: contain;
  }
`;

const DefaultLogo = styled.div`
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #0ea5e9, #0284c7);
  border-radius: 50%;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 36px;
  font-weight: bold;
`;

const FormTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 20px;
  line-height: 1.2;
`;

const FormDescription = styled.p`
  font-size: 16px;
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 40px;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
  margin-bottom: 50px;
`;

const FeatureCard = styled.div`
  padding: 20px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #0ea5e9, #0284c7);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  color: white;
  font-size: 20px;
`;

const FeatureTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const FeatureDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.4;
`;

const StartButton = styled.button`
  background: linear-gradient(135deg, #0ea5e9, #0284c7);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 18px 40px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 auto;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(14, 165, 233, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(14, 165, 233, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const FormInfo = styled.div`
  background: #f1f5f9;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
  border-left: 4px solid #0ea5e9;
`;

const FormInfoTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const FormInfoText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

function FormLanding({ formId }) {
  const { id } = useParams();
  const actualFormId = formId || id;
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    loadForm();
  }, [actualFormId]);

  const loadForm = async () => {
    try {
      setLoading(true);
        const response = await formsAPI.getShared(actualFormId);
        console.log('Form data loaded:', response.data.data);
        setForm(response.data.data);
    } catch (error) {
      toast.error('Failed to load form');
      console.error('Load form error:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleStartForm = async () => {
    setStarting(true);
    
    // Add a small delay for better UX
    setTimeout(() => {
      navigate(`/form/${id}`);
    }, 500);
  };

  if (loading) {
    return (
      <LandingContainer>
        <LandingCard>
          <LoadingSpinner />
          <p style={{ marginTop: '20px', color: '#6b7280' }}>Loading form...</p>
        </LandingCard>
      </LandingContainer>
    );
  }

  if (!form) {
    return (
      <LandingContainer>
        <LandingCard>
          <h1>Form Not Found</h1>
          <p>The requested form could not be found.</p>
        </LandingCard>
      </LandingContainer>
    );
  }

  return (
    <LandingContainer>
      <LandingCard>
        <LogoContainer>
          {form.logo_url ? (
            <img 
              src={form.logo_url} 
              alt="Company Logo" 
              onError={(e) => {
                console.error('Failed to load logo:', form.logo_url);
                e.target.style.display = 'none';
              }}
              onLoad={() => console.log('Logo loaded successfully:', form.logo_url)}
            />
          ) : (
            <DefaultLogo>
              {form.title.charAt(0).toUpperCase()}
            </DefaultLogo>
          )}
        </LogoContainer>

        <FormTitle>{form.title}</FormTitle>
        
        <FormDescription>
          {form.description || 'Please fill out this form to help us gather the information we need. Your responses are important to us and will help us provide better service.'}
        </FormDescription>

        <FormInfo>
          <FormInfoTitle>Form Information</FormInfoTitle>
          <FormInfoText>
            This form contains {form.form_structure?.length || 0} fields and typically takes 3-5 minutes to complete.
          </FormInfoText>
        </FormInfo>

        <FeaturesGrid>
          <FeatureCard>
            <FeatureIcon>
              <FiShield />
            </FeatureIcon>
            <FeatureTitle>Secure & Private</FeatureTitle>
            <FeatureDescription>
              Your information is encrypted and stored securely
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <FiClock />
            </FeatureIcon>
            <FeatureTitle>Quick & Easy</FeatureTitle>
            <FeatureDescription>
              Simple form that takes just a few minutes to complete
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <FiUsers />
            </FeatureIcon>
            <FeatureTitle>Helpful</FeatureTitle>
            <FeatureDescription>
              Your feedback helps us improve our services
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>

        <StartButton onClick={handleStartForm} disabled={starting}>
          {starting ? (
            <>
              <LoadingSpinner />
              Starting...
            </>
          ) : (
            <>
              Start Form
              <FiArrowRight />
            </>
          )}
        </StartButton>
      </LandingCard>
    </LandingContainer>
  );
}

export default FormLanding;
